import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LocationFeedPage.scss';
import { thumbImgProps } from "../../utils/imageUrl";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  '';

// Human-facing counterpart to cvanto-ssr's pages/city/[citySlug]/index.js,
// pages/city/[citySlug]/[nodeSlug].js and pages/category/[slug]/[subSlug].js.
//
// Deliberately NOT built on top of Feed.js (the main infinite-scroll feed):
// Feed.js only understands `categoryName`, has no concept of city/
// subcategory filtering, and is large/stateful/cache-keyed code already
// carrying real production traffic — extending it here would be exactly
// the kind of risky, wide-blast-radius change the SEO rollout was told to
// avoid. Instead this fetches the same public seoLocationService
// aggregation endpoint the SSR bot pages already use
// (server/services/seoLocationService.js), so a human landing on
// /city/ternopil/psykholoh from a Google result sees the SAME real
// listings/profiles a crawler was already shown for that URL — genuine
// content parity, not a second, divergent implementation.
function toMoney(price, currency) {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return '';
  const cur = String(currency || 'UAH').toUpperCase() === 'UAH' ? 'грн' : currency;
  return `${n.toLocaleString('uk-UA')} ${cur}`;
}

const LocationFeedPage = () => {
  const { citySlug, nodeSlug, slug: categorySlugParam, subSlug } = useParams();
  const navigate = useNavigate();

  const isCategoryMode = !citySlug && !!categorySlugParam && !!subSlug;
  const apiPath = isCategoryMode
    ? `/api/seo/category-node/${encodeURIComponent(categorySlugParam)}/${encodeURIComponent(subSlug)}`
    : nodeSlug
      ? `/api/seo/location/${encodeURIComponent(citySlug)}/${encodeURIComponent(nodeSlug)}`
      : `/api/seo/location/${encodeURIComponent(citySlug)}`;

  const [agg, setAgg] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'notfound'

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setAgg(null);

    fetch(`${API_BASE}${apiPath}`)
      .then((r) => {
        if (r.status === 404) throw new Error('not_found');
        if (!r.ok) throw new Error('failed');
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setAgg(data);
        setStatus('ok');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('notfound');
      });

    return () => {
      cancelled = true;
    };
  }, [apiPath]);

  useEffect(() => {
    if (status === 'notfound') {
      navigate('/', { replace: true });
    }
  }, [status, navigate]);

  if (status === 'loading' || status === 'notfound' || !agg) {
    return null;
  }

  const cityName = agg.cityName || '';
  const cityLocative = agg.cityLocative || cityName;
  const categoryName = agg.categoryName || '';
  const node = agg.node || null;
  const eligible = !!agg.eligible;
  const listings = Array.isArray(agg.listings) ? agg.listings : [];
  const profiles = Array.isArray(agg.profiles) ? agg.profiles : [];

  const label = node ? (node.type === 'subcategory' ? node.namePlural : node.name) : '';
  const h1 = node
    ? (cityLocative ? `${label} в ${cityLocative}` : label)
    : `${cityName} — оголошення та спеціалісти`;
  const seoTitle = node
    ? (node.type === 'subcategory'
        ? `${label}${cityLocative ? ` в ${cityLocative}` : ''} — знайти ${node.name.toLowerCase()} | Cvanto`
        : `${node.name}${cityLocative ? ` в ${cityLocative}` : ''} — оголошення на Cvanto`)
    : `Оголошення та спеціалісти в ${cityLocative} | Cvanto`;
  const seoDesc = node
    ? (node.type === 'subcategory'
        ? `${label}${cityLocative ? ` у ${cityLocative}` : ''} на Cvanto — профілі спеціалістів, послуги, ціни та актуальні пропозиції.`
        : `Оголошення в категорії «${node.name}»${cityLocative ? ` у ${cityLocative}` : ''} на Cvanto.`)
    : `Знайдіть оголошення, послуги та перевірених спеціалістів у ${cityLocative} на Cvanto.`;

  const seoUrl = `https://cvanto.com${window.location.pathname}`;
  const parentUrl = agg.parentUrl ? `https://cvanto.com${agg.parentUrl}` : null;
  const canonicalUrl = eligible ? seoUrl : (parentUrl || seoUrl);

  return (
    <div className="location-feed">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonicalUrl} />
        {!eligible ? <meta name="robots" content="noindex,follow" /> : null}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cvanto" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content="https://cvanto.com/web-app-manifest-512x512.png" />
      </Helmet>

      <div className="location-feed__header">
        <nav className="location-feed__breadcrumb" aria-label="breadcrumb">
          <Link to="/">Cvanto</Link>
          {citySlug && (
            <>
              {' › '}
              {nodeSlug ? <Link to={`/city/${citySlug}`}>{cityName}</Link> : <span>{cityName}</span>}
            </>
          )}
          {isCategoryMode && (
            <>
              {' › '}
              <Link to={`/category/${categorySlugParam}`}>{categoryName}</Link>
            </>
          )}
          {node && (
            <>
              {' › '}
              <span>{label}</span>
            </>
          )}
        </nav>

        <h1>{h1}</h1>
      </div>

      {!eligible ? (
        <p className="location-feed__empty">
          Поки що тут недостатньо оголошень чи профілів.{' '}
          {parentUrl ? <Link to={agg.parentUrl}>Перегляньте більше на Cvanto</Link> : null}
        </p>
      ) : (
        <>
          {listings.length > 0 && (
            <div className="location-feed__grid">
              {listings.map((it) => (
                <Link key={it.id} to={`/listing/${it.id}`} className="location-feed__card">
                  {Array.isArray(it.images) && it.images[0] ? (
                    <img {...thumbImgProps(it.images[0])} alt={it.title || 'Оголошення'} />
                  ) : null}
                  <div className="location-feed__card-body">
                    <div className="location-feed__card-title">{it.title || 'Оголошення'}</div>
                    <div className="location-feed__card-price">{toMoney(it.price, it.currency)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {profiles.length > 0 && (
            <div className="location-feed__grid">
              {profiles.map((p) => (
                <Link key={p.id} to={`/profile/${p.id}`} className="location-feed__card location-feed__card--profile">
                  {p.username || 'Профіль на Cvanto'}
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LocationFeedPage;
